import _ from "lodash";
import moment from "moment";
import { DAYS_IN_ORDER } from "Utils";
import { getSettingValue } from "modules/PermissionModule";
import { ATTENDANCE_CONSTANTS } from "Constants/attendanceConstants";
import { ATTENDANCE_RECORDS_VIEW_TYPE } from "AttendanceSummary/modules/AttendanceSummaryModule";
import { ATTENDANCE_CONSTANTS as ATTENDANCE_STRING_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services";
import { formatDate, checkIsDateAfter } from "Utils/DateTimeHelpers";
import ACLStore from "lib/aclStore";
import { colors } from "@toddle-edu/ds-theme";

const currentDate = moment().format(ATTENDANCE_CONSTANTS.YYYY_MM_DD);

export const getAttendanceSettingsV2Enabled = () => {
  return ACLStore.can("FeatureFlag:AttendanceSettingsV2");
};

export const getPastExcusals = ({ studentExcusals }) => {
  return _.filter(studentExcusals, excusal => {
    const endDate = _.get(excusal, "node.endDate", "");
    if (moment(endDate).isBefore(currentDate, "day")) {
      return excusal;
    }
  });
};

export const getOngoingExcusals = ({ studentExcusals }) => {
  return _.filter(studentExcusals, excusal => {
    const startDate = _.get(excusal, "node.startDate", "");
    const endDate = _.get(excusal, "node.endDate", "");
    if (moment(currentDate).isBetween(startDate, endDate, "day", "[]")) {
      return excusal;
    }
  });
};

export const getUpcomingExcusals = ({ studentExcusals }) => {
  return _.filter(studentExcusals, excusal => {
    const startDate = _.get(excusal, "node.startDate", "");
    if (moment(startDate).isAfter(currentDate, "day")) {
      return excusal;
    }
  });
};

export const isUserTypeStaff = ({ userType }) => {
  return userType === ATTENDANCE_GLOBAL_CONSTANTS.STAFF;
};

export const isUserTypeParent = ({ userType }) => {
  return userType === ATTENDANCE_GLOBAL_CONSTANTS.PARENT;
};

export const isUserTypeStudent = ({ userType }) => {
  return userType === ATTENDANCE_GLOBAL_CONSTANTS.STUDENT;
};

export const getStudentCourses = ({ studentCourses }) => {
  return _.map(studentCourses, ({ id, title }) => {
    return {
      id,
      label: title,
    };
  });
};

export const getOperationDayBetweenDates = (
  fromDate,
  toDate,
  operationalDays,
  holidayEvents = []
) => {
  const selectedStartDate = moment(fromDate);
  const selectedEndDate = moment(toDate);
  let NoOfOperationalDay = 0;

  // Pre-parse holiday dates once to avoid repeated moment() calls in the loop
  const parsedHolidays = _.map(holidayEvents, holiday => ({
    start: moment(_.get(holiday, "start")).startOf("day"),
    end: moment(_.get(holiday, "end")).startOf("day"),
  }));

  while (selectedStartDate.isSameOrBefore(selectedEndDate)) {
    // Check if current date is a holiday
    const isHoliday = _.some(parsedHolidays, ({ start, end }) => {
      return selectedStartDate.isBetween(start, end, "day", "[]");
    });

    // Only count if it's an operational day and not a holiday
    if (!isHoliday) {
      const dayNumberInWeek = _.indexOf(
        DAYS_IN_ORDER,
        moment(selectedStartDate).locale("en").format("ddd")
      );
      NoOfOperationalDay += _.includes(operationalDays, dayNumberInWeek);
    }

    selectedStartDate.add(1, "days");
  }
  return NoOfOperationalDay;
};

export const getIsSelectedDateRangeContainsOnlyNonOperationalDays = ({
  durationType,
  selectedDate,
  toDate,
  operationalDays,
  holidayEvents = [],
}) => {
  let startDate, endDate;
  switch (durationType) {
    case ATTENDANCE_STRING_CONSTANTS.SINGLE_DAY:
    case ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY: {
      startDate = moment(selectedDate);
      endDate = moment(selectedDate);
      break;
    }
    case ATTENDANCE_STRING_CONSTANTS.MULTIPLE_DAYS: {
      startDate = moment(selectedDate);
      endDate = moment(toDate);
      break;
    }
  }

  return _.isEqual(
    getOperationDayBetweenDates(
      startDate,
      endDate,
      operationalDays,
      holidayEvents
    ),
    0
  );
};

const updateAttendanceMutationHandle = ({
  attedanceMutationData,
  upsertCourseAttendanceStudentTab,
}) => {
  upsertCourseAttendanceStudentTab(attedanceMutationData);
};

export const updateAttendanceOptionDebounce = _.debounce(
  updateAttendanceMutationHandle,
  1000
);

export const getFormattedDayAndPeriodText = ({
  rotationalDayNumber,
  periodName,
}) => {
  let dayText = "";
  if (rotationalDayNumber) {
    dayText = rotationalDayNumber;
  }

  if (rotationalDayNumber && periodName) {
    return `${dayText}
    \xB7 ${periodName}`;
  } else if (rotationalDayNumber) {
    return `${dayText}`;
  } else if (periodName) {
    `${periodName}`;
  } else {
    return ``;
  }
};

export const getExcusalRotationalNumber = ({
  excusal,
  rotationDaysHashMap,
}) => {
  const { durationType, startDate, endDate } = excusal;
  const formattedStartDate = formatDate({
    date: startDate,
    format: ATTENDANCE_STRING_CONSTANTS.YYYY_MM_DD,
  });

  const formattedEndDate = formatDate({
    date: endDate,
    format: ATTENDANCE_STRING_CONSTANTS.YYYY_MM_DD,
  });

  const startDateRotationalDay = _.get(
    rotationDaysHashMap,
    `${formattedStartDate}`,
    null
  );

  const EndDateRotationalDay = _.get(
    rotationDaysHashMap,
    `${formattedEndDate}`,
    null
  );

  switch (durationType) {
    case ATTENDANCE_STRING_CONSTANTS.MULTIPLE_DAYS: {
      if (startDateRotationalDay && EndDateRotationalDay) {
        return `${startDateRotationalDay} - ${EndDateRotationalDay}`;
      } else {
        return null;
      }
    }
    case ATTENDANCE_STRING_CONSTANTS.SINGLE_DAY:
    case ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY: {
      if (startDateRotationalDay) {
        return startDateRotationalDay;
      } else {
        return null;
      }
    }
  }
};

export const getPeriodIncluded = ({ excusalPeriods, periodId }) => {
  return _.find(excusalPeriods, ({ id }) => {
    return id === periodId;
  });
};

export const checkExcusalIsPartialDay = durationType => {
  return durationType === ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY;
};

export const checkViewTypeCalendar = viewType => {
  return viewType === ATTENDANCE_RECORDS_VIEW_TYPE.CALENDAR;
};

export const isTeacherPortal = portal => {
  return portal === ATTENDANCE_CONSTANTS.TEACHER_PORTAL;
};

export const isStudentUserType = userType => {
  return userType === ATTENDANCE_CONSTANTS.STUDENT;
};

export const isParentUserType = userType => {
  return userType === ATTENDANCE_CONSTANTS.PARENT;
};

export const isStaffUserType = userType => {
  return userType === ATTENDANCE_CONSTANTS.STAFF;
};

export const checkUserTeacherOrAdmin = userType => {
  return !isStudentUserType(userType) && !isParentUserType(userType);
};

export const getTimetableEndDate = ({
  selectedAcademicYearEndDate,
  endDate,
}) => {
  return selectedAcademicYearEndDate &&
    checkIsDateAfter({ date1: endDate, date2: selectedAcademicYearEndDate })
    ? selectedAcademicYearEndDate
    : endDate;
};

export const getSegmentedProgressByStep = ({ step }) => {
  const step1Color = _.includes(["STEP_1"], step)
    ? colors.surfaceDark
    : colors.neutral600;
  const step2Color = _.includes(["STEP_2"], step)
    ? colors.surfaceDark
    : colors.neutral600;

  return [
    {
      id: "STEP_1",
      color: step1Color,
      progress: 49.5,
    },
    {
      id: "GAP_1",
      color: "#ffffff",
      progress: 1,
    },
    {
      id: "STEP_2",
      color: step2Color,
      progress: 49.5,
    },
  ];
};

export const getCanParentModifyExcusal = ({
  endDate,
  attendanceOption,
  curriculumProgramId,
}) => {
  const excusalToUpdateByType = getSettingValue({
    name: "Setting:AllowExcusalToUpdateByType",
    currentCurriculumProgramId: curriculumProgramId,
  });
  const excusalToUpdateByDate = getSettingValue({
    name: "Setting:AllowExcusalToUpdateByDate",
    currentCurriculumProgramId: curriculumProgramId,
  });

  const now = moment();
  const canModifyByType = (() => {
    switch (excusalToUpdateByType) {
      case "NONE":
        return false;
      case "MARKED":
        return attendanceOption && _.get(attendanceOption, "id");
      case "NOT_MARKED":
        return _.isNil(attendanceOption);
      case "ALL":
        return true;
      default:
        return false;
    }
  })();

  const canModifyByDate = (() => {
    switch (excusalToUpdateByDate) {
      case "NONE":
        return false;
      case "ANY_DATES":
        return true;
      case "CURRENT_AND_FUTURE_DATES":
        return !moment(endDate).isBefore(now, "day");
      default:
        return false;
    }
  })();

  return canModifyByType && canModifyByDate;
};

export const getAttendanceCodeWithCategoryDropdownOptions = ({
  selectedCategory,
  attendanceCategories,
  allCodesText,
}) => {
  const selectedCategoryOptions = _.get(
    _.find(attendanceCategories, ({ id }) => id === selectedCategory),
    "attendanceOptions"
  );

  return [
    {
      key: "allFilters",
      label: allCodesText,
    },
    ..._.map(selectedCategoryOptions, ({ id, label }) => {
      const newItem = {
        key: `${selectedCategory}-${id}`,
        label,
      };
      return newItem;
    }),
  ];
};

export const getAttendanceCategoryDropdownOptions = ({
  attendanceCategories,
  allCodesText,
}) => {
  return [
    {
      key: "allFilters",
      label: allCodesText,
    },
    ..._.map(attendanceCategories, ({ id, label, attendanceOptions }) => {
      const newItem = {
        key: id,
        label,
        isItemGroup: true,
        options: _.map(attendanceOptions, ({ id: optionId, label }) => ({
          key: `${id}-${optionId}`,
          label,
        })),
      };
      return newItem;
    }),
  ];
};

const getAttendanceCategoryOptions = ({
  attendanceCategoriesOptions,
  attendanceCategoriesPercentage,
}) => {
  const modifiedCategories = _.map(
    attendanceCategoriesOptions,
    ({ id, label, color }) => {
      const percentageObj = _.find(
        attendanceCategoriesPercentage,
        ({ category }) => _.get(category, "id") === id
      );
      return {
        id,
        label,
        color,
        percentage: percentageObj ? _.get(percentageObj, "percentage") : "0",
      };
    }
  );

  _.forEach(attendanceCategoriesPercentage, ({ category, percentage }) => {
    const { id, label, color } = category;

    const extraAttendanceCategory = _.some(
      modifiedCategories,
      ({ id: optionId }) => optionId === id
    );
    if (!extraAttendanceCategory) {
      modifiedCategories.push({
        id,
        label,
        color,
        percentage,
      });
    }
  });

  return modifiedCategories;
};

export const getAttendanceCategoryOptionsMemoized = _.memoize(
  params => getAttendanceCategoryOptions(params),
  params => JSON.stringify(params)
);

const getRotationDaysHashMap = ({ routinesByDate = [] }) => {
  return _.reduce(
    routinesByDate,
    (rotationDaysHashMap, rotationDayData) => {
      const date = _.get(rotationDayData, "date", "");
      const rotationDayNumber = _.get(
        rotationDayData,
        "rotationDay.label",
        null
      );

      rotationDaysHashMap[`${date}`] = rotationDayNumber;

      return rotationDaysHashMap;
    },
    {}
  );
};

export const getRotationDaysHashMapMemoize = _.memoize(
  params => getRotationDaysHashMap(params),
  params => {
    const { routinesByDate } = params;

    const mappedArray = _.map(routinesByDate, d => {
      const date = _.get(d, "date");
      const rotationDay = _.get(d, "rotationDay.label");
      return `${date}:${rotationDay}`;
    });

    const datesKey = _.join(mappedArray, "-");

    return `${datesKey}`;
  }
);

export const getPartialDayExcusalConflict = ({
  selectedDate,
  selectedPeriods,
  conflictedDates,
  conflictedDatePeriodMap,
}) => {
  const selectedDateFormatted = moment(selectedDate).format("YYYY-MM-DD");
  const conflictedPeriodIdsForDate = _.get(
    conflictedDatePeriodMap,
    selectedDateFormatted,
    []
  );

  const hasDateConflict = _.includes(conflictedDates, selectedDateFormatted);

  const hasPeriodConflict = _.some(selectedPeriods, periodId =>
    _.includes(conflictedPeriodIdsForDate, periodId)
  );

  const shouldShowDuplicateWarning = _.isEmpty(conflictedPeriodIdsForDate)
    ? hasDateConflict
    : hasPeriodConflict;

  return shouldShowDuplicateWarning;
};

export const checkIsExcusalDuplicate = ({
  selectedStudentsExcusalDetails,
  durationType,
  selectedDate,
  selectedPeriods,
  toDate,
  excusalId,
  initialExcusalData,
}) => {
  if (_.isEmpty(selectedStudentsExcusalDetails)) {
    return [];
  }

  const DATE_FORMAT = "YYYY-MM-DD";
  const selectedStartDate = moment(selectedDate);
  const selectedEndDate = moment(toDate);
  const formatDate = date => moment(date).format(DATE_FORMAT);

  const addPartialDayPeriods = ({ map, date, excusal }) => {
    const periodIds = _.map(_.get(excusal, "periods", []), "id");
    _.update(map, date, (existingIds = []) =>
      _.uniq([...existingIds, ...periodIds])
    );
  };

  // For edit excusal, if dates are unchanged, no need to check for duplicates
  if (excusalId && initialExcusalData) {
    const isSameDurationType = _.isEqual(
      durationType,
      _.get(initialExcusalData, "durationType")
    );

    const isSameDates =
      _.isEqual(
        formatDate(selectedStartDate),
        formatDate(initialExcusalData.startDate)
      ) &&
      _.isEqual(
        formatDate(selectedEndDate),
        formatDate(initialExcusalData.endDate)
      );

    if (isSameDurationType && isSameDates) {
      const isPartialDay = durationType === "PARTIAL_DAY";
      const initialPeriodIds = _.get(initialExcusalData, "periodIds", []);

      const isSamePeriods = _.isEqual(
        _.sortBy(selectedPeriods),
        _.sortBy(initialPeriodIds)
      );

      if (!isPartialDay || isSamePeriods) {
        return [];
      }
    }
  }

  let conflictedDates = [];
  const conflictedDatePeriodMap = {};

  const studentsArray = _.isArray(selectedStudentsExcusalDetails)
    ? selectedStudentsExcusalDetails
    : [{ node: selectedStudentsExcusalDetails }];

  _.forEach(studentsArray, studentEdge => {
    const student = _.has(studentEdge, "node")
      ? _.get(studentEdge, "node")
      : studentEdge;

    const existingExcusalEdges = _.get(
      student,
      "studentAttendanceExcusals.edges",
      []
    );

    _.forEach(existingExcusalEdges, ({ node: existingExcusal }) => {
      if (excusalId && existingExcusal.id === excusalId) return;

      const excusalStartDate = moment(existingExcusal.startDate);
      const excusalEndDate = moment(existingExcusal.endDate);
      const excusalDurationType = existingExcusal.durationType;

      let localConflictedDates = [];
      const localConflictedDatePeriodMap = {};

      if (durationType === ATTENDANCE_STRING_CONSTANTS.MULTIPLE_DAYS) {
        const noDateOverlap =
          selectedEndDate.isBefore(excusalStartDate, "day") ||
          excusalEndDate.isBefore(selectedStartDate, "day");

        if (!noDateOverlap) {
          const overlapStartDate = moment.max(
            selectedStartDate,
            excusalStartDate
          );
          const overlapEndDate = moment.min(selectedEndDate, excusalEndDate);

          const currentDate = overlapStartDate.clone();
          while (currentDate.isSameOrBefore(overlapEndDate, "day")) {
            localConflictedDates.push(formatDate(currentDate));
            currentDate.add(1, "day");
          }
          if (excusalDurationType === "PARTIAL_DAY") {
            addPartialDayPeriods({
              map: localConflictedDatePeriodMap,
              date: formatDate(excusalStartDate),
              excusal: existingExcusal,
            });
          }
        }
      } else {
        const selectedDateFormatted = formatDate(selectedStartDate);
        if (
          _.get(existingExcusal, "durationType") ===
          ATTENDANCE_STRING_CONSTANTS.MULTIPLE_DAYS
        ) {
          if (
            selectedStartDate.isSameOrBefore(excusalEndDate, "day") &&
            selectedStartDate.isSameOrAfter(excusalStartDate, "day")
          ) {
            localConflictedDates = [selectedDateFormatted];
            if (
              excusalDurationType === ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY
            ) {
              addPartialDayPeriods({
                map: localConflictedDatePeriodMap,
                date: selectedDateFormatted,
                excusal: existingExcusal,
              });
            }
          }
        } else {
          const excusalDateFormatted = formatDate(excusalStartDate);
          if (selectedDateFormatted === excusalDateFormatted) {
            localConflictedDates = [selectedDateFormatted];
            if (
              excusalDurationType === ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY
            ) {
              addPartialDayPeriods({
                map: localConflictedDatePeriodMap,
                date: selectedDateFormatted,
                excusal: existingExcusal,
              });
            }
          }
        }
      }

      if (!_.isEmpty(localConflictedDates)) {
        conflictedDates = _.uniq([...conflictedDates, ...localConflictedDates]);
      }
      if (!_.isEmpty(localConflictedDatePeriodMap)) {
        _.forEach(localConflictedDatePeriodMap, (periodIds, date) => {
          _.update(conflictedDatePeriodMap, date, (existingIds = []) =>
            _.uniq([...existingIds, ...periodIds])
          );
        });
      }
    });
  });

  return {
    newConflictedDates: conflictedDates,
    newConflictedDatePeriodMap: conflictedDatePeriodMap,
  };
};

export const getExcusalDuplicateConflict = ({
  durationType,
  selectedDate,
  toDate,
  selectedPeriods,
  conflictedDates,
  conflictedDatePeriodMap,
}) => {
  const formattedSelectedDate = moment(selectedDate).format("YYYY-MM-DD");

  switch (durationType) {
    case ATTENDANCE_STRING_CONSTANTS.MULTIPLE_DAYS: {
      if (_.isNil(toDate)) {
        return _.includes(conflictedDates, formattedSelectedDate);
      }

      const start = moment(selectedDate);
      const end = moment(toDate);
      const daysDiff = end.diff(start, "days");

      const dateRange = _.range(daysDiff + 1);

      const hasConflictInRange = _.some(dateRange, offset => {
        const currentDate = moment(start)
          .add(offset, "day")
          .format("YYYY-MM-DD");

        return _.includes(conflictedDates, currentDate);
      });

      return hasConflictInRange;
    }

    case ATTENDANCE_STRING_CONSTANTS.SINGLE_DAY:
      return _.includes(conflictedDates, formattedSelectedDate);

    case ATTENDANCE_STRING_CONSTANTS.PARTIAL_DAY:
      return getPartialDayExcusalConflict({
        selectedDate,
        selectedPeriods,
        conflictedDates,
        conflictedDatePeriodMap,
      });

    default:
      return false;
  }
};
